import { DeleteOutlined, DownloadOutlined, EditOutlined, FileSearchOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Typography, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import type { InsuranceType, TenderTemplate } from './types';

const { Text } = Typography;

type TemplateFormValues = {
  name: string;
  notes?: string;
  insuranceTypeIds: string[];
  isActive: boolean;
};

const statusLabel: Record<TenderTemplate['analysisStatus'], string> = {
  NOT_ANALYZED: 'Ikke analyseret',
  COMPLETED: 'Klar',
  FAILED: 'Fejlet'
};

const statusColor: Record<TenderTemplate['analysisStatus'], string> = {
  NOT_ANALYZED: 'default',
  COMPLETED: 'success',
  FAILED: 'error'
};

function normalizeTemplate(raw: any): TenderTemplate {
  return {
    id: String(raw.id || ''),
    name: raw.name || '',
    notes: raw.notes ?? null,
    fileName: raw.fileName ?? raw.file_name ?? '',
    fileType: raw.fileType ?? raw.file_type ?? null,
    fileSize: raw.fileSize ?? raw.file_size ?? null,
    isActive: raw.isActive !== false,
    analysisStatus: raw.analysisStatus ?? raw.analysis_status ?? 'NOT_ANALYZED',
    analysisError: raw.analysisError ?? raw.analysis_error ?? null,
    analyzedAt: raw.analyzedAt ?? raw.analyzed_at ?? null,
    analyzedBy: raw.analyzedBy ?? raw.analyzed_by ?? null,
    createdAt: raw.createdAt ?? raw.created_at ?? '',
    updatedAt: raw.updatedAt ?? raw.updated_at ?? '',
    insuranceTypes: Array.isArray(raw.insuranceTypes ?? raw.insurance_types) ? raw.insuranceTypes ?? raw.insurance_types : [],
    fieldCount: Number(raw.fieldCount ?? raw.field_count ?? 0),
    downloadUrl: raw.downloadUrl ?? `/api/tender-templates/${raw.id}/download`
  };
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('da-DK', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export default function TenderTemplatesPage() {
  const [templates, setTemplates] = useState<TenderTemplate[]>([]);
  const [insuranceTypes, setInsuranceTypes] = useState<InsuranceType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TenderTemplate | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [form] = Form.useForm<TemplateFormValues>();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [templateResponse, typeResponse] = await Promise.all([api('/api/tender-templates'), api('/api/insurance-types')]);
      const templateData = await templateResponse.json().catch(() => ({}));
      const typeData = await typeResponse.json().catch(() => ([]));
      if (!templateResponse.ok) throw new Error(templateData?.error || 'Kunne ikke hente udbudsskabeloner');
      if (!typeResponse.ok) throw new Error(typeData?.error || 'Kunne ikke hente forsikringstyper');
      setTemplates(Array.isArray(templateData) ? templateData.map(normalizeTemplate) : []);
      setInsuranceTypes(Array.isArray(typeData) ? typeData : []);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente udbudsskabeloner');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
    setFileList([]);
    form.resetFields();
  }, [form]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setFileList([]);
    form.setFieldsValue({ name: '', notes: '', insuranceTypeIds: [], isActive: true });
    setModalOpen(true);
  }, [form]);

  const openEdit = useCallback((template: TenderTemplate) => {
    setEditing(template);
    setFileList([]);
    form.setFieldsValue({
      name: template.name,
      notes: template.notes || '',
      insuranceTypeIds: template.insuranceTypes.map((item) => String(item.id)),
      isActive: template.isActive
    });
    setModalOpen(true);
  }, [form]);

  const save = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name.trim(),
        notes: values.notes?.trim() || null,
        insuranceTypeIds: values.insuranceTypeIds,
        isActive: values.isActive
      };
      setSaving(true);
      let data: any;
      if (editing) {
        const metadataResponse = await api(`/api/tender-templates/${editing.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        data = await metadataResponse.json().catch(() => ({}));
        if (!metadataResponse.ok) throw new Error(data?.error || 'Kunne ikke opdatere udbudsskabelon');
        const replacement = fileList[0]?.originFileObj;
        if (replacement) {
          const formData = new FormData();
          formData.append('file', replacement);
          const replacementResponse = await api(`/api/tender-templates/${editing.id}/file`, { method: 'POST', body: formData });
          data = await replacementResponse.json().catch(() => ({}));
          if (!replacementResponse.ok) throw new Error(data?.error || 'Kunne ikke udskifte Excel-skabelonen');
        }
      } else {
        const file = fileList[0]?.originFileObj;
        if (!file) {
          message.warning('Vælg en Excel-skabelon');
          return;
        }
        const formData = new FormData();
        formData.append('name', payload.name);
        if (payload.notes) formData.append('notes', payload.notes);
        formData.append('insuranceTypeIds', JSON.stringify(payload.insuranceTypeIds));
        formData.append('file', file);
        const response = await api('/api/tender-templates', { method: 'POST', body: formData });
        data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.error || 'Kunne ikke gemme udbudsskabelon');
      }
      message.success(editing ? 'Udbudsskabelon opdateret' : 'Udbudsskabelon uploadet');
      closeModal();
      await loadData();
    } catch (error: any) {
      if (!error?.errorFields) message.error(error?.message || 'Kunne ikke gemme udbudsskabelon');
    } finally {
      setSaving(false);
    }
  }, [closeModal, editing, fileList, form, loadData]);

  const analyze = useCallback(async (template: TenderTemplate) => {
    setAnalyzingId(template.id);
    try {
      const response = await api(`/api/tender-templates/${template.id}/analyze`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'Kunne ikke analysere udbudsskabelonen');
      message.success(`Skabelonen er analyseret (${Number(data.fieldCount || 0)} felter)`);
      await loadData();
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke analysere udbudsskabelonen');
    } finally {
      setAnalyzingId(null);
    }
  }, [loadData]);

  const download = useCallback(async (template: TenderTemplate) => {
    try {
      const response = await api(template.downloadUrl);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || 'Kunne ikke hente skabelonen');
      }
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = template.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente skabelonen');
    }
  }, []);

  const remove = useCallback(async (template: TenderTemplate) => {
    try {
      const response = await api(`/api/tender-templates/${template.id}`, { method: 'DELETE' });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || 'Kunne ikke slette udbudsskabelonen');
      }
      message.success('Udbudsskabelon slettet');
      await loadData();
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke slette udbudsskabelonen');
    }
  }, [loadData]);

  const columns: ColumnsType<TenderTemplate> = [
    { title: 'Skabelon', dataIndex: 'name', key: 'name', render: (_, template) => <Space direction="vertical" size={0}><Text strong>{template.name}</Text><Text type="secondary">{template.fileName}</Text></Space> },
    { title: 'Forsikringstyper', key: 'insuranceTypes', render: (_, template) => <Space size={[4, 4]} wrap>{template.insuranceTypes.map((item) => <Tag key={item.id}>{item.title}</Tag>)}</Space> },
    { title: 'Analyse', key: 'analysis', render: (_, template) => <Space direction="vertical" size={2}><Tag color={statusColor[template.analysisStatus]}>{statusLabel[template.analysisStatus]}</Tag><Text type="secondary">{template.analysisStatus === 'COMPLETED' ? `${template.fieldCount} felter · ${formatDate(template.analyzedAt)}` : template.analysisError || 'Kræver manuel analyse'}</Text></Space> },
    { title: 'Aktiv', dataIndex: 'isActive', key: 'isActive', render: (active) => <Tag color={active ? 'success' : 'default'}>{active ? 'Aktiv' : 'Inaktiv'}</Tag> },
    { title: 'Handlinger', key: 'actions', width: 240, render: (_, template) => <Space size={2}><Button type="text" icon={<DownloadOutlined />} title="Hent skabelon" onClick={() => void download(template)} /><Button type="text" icon={<EditOutlined />} title="Rediger skabelon" onClick={() => openEdit(template)} /><Button type="text" icon={<FileSearchOutlined />} title="Analyser Excel-struktur" loading={analyzingId === template.id} onClick={() => void analyze(template)} /><Popconfirm title="Slet udbudsskabelon" description={`Slet ${template.name}?`} okText="Slet" cancelText="Annuller" onConfirm={() => void remove(template)}><Button type="text" danger icon={<DeleteOutlined />} title="Slet skabelon" /></Popconfirm></Space> }
  ];

  return <Card title="Udbudsskabeloner" extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Ny skabelon</Button>}>
    <Table rowKey="id" loading={loading} columns={columns} dataSource={templates} pagination={{ pageSize: 20, showSizeChanger: false }} />
    <Modal open={modalOpen} title={editing ? 'Rediger udbudsskabelon' : 'Ny udbudsskabelon'} okText="Gem" cancelText="Annuller" onOk={() => void save()} onCancel={closeModal} confirmLoading={saving} destroyOnClose maskClosable={false}>
      <Form form={form} layout="vertical" initialValues={{ isActive: true, insuranceTypeIds: [] }}>
        <Form.Item name="name" label="Navn" rules={[{ required: true, message: 'Angiv et navn' }]}><Input maxLength={200} autoFocus /></Form.Item>
        {!editing ? <Form.Item label="Excel-skabelon" required><Upload accept=".xlsx" maxCount={1} fileList={fileList} beforeUpload={(file) => { if (!file.name.toLowerCase().endsWith('.xlsx') || file.size > 25 * 1024 * 1024) { message.error('Vælg en .xlsx-fil på højst 25 MB'); return Upload.LIST_IGNORE; } return false; }} onChange={({ fileList: next }) => setFileList(next.slice(-1))}><Button icon={<UploadOutlined />}>Vælg Excel-fil</Button></Upload></Form.Item> : <Form.Item label="Excel-skabelon"><Space direction="vertical" size={4}><Text>{editing.fileName}</Text><Upload accept=".xlsx" maxCount={1} fileList={fileList} beforeUpload={(file) => { if (!file.name.toLowerCase().endsWith('.xlsx') || file.size > 25 * 1024 * 1024) { message.error('Vælg en .xlsx-fil på højst 25 MB'); return Upload.LIST_IGNORE; } return false; }} onChange={({ fileList: next }) => setFileList(next.slice(-1))}><Button icon={<UploadOutlined />}>Erstat Excel-fil</Button></Upload>{fileList.length ? <Text type="warning">Skabelonen skal analyseres igen efter filudskiftning.</Text> : null}</Space></Form.Item>}
        <Form.Item name="insuranceTypeIds" label="Forsikringstyper" rules={[{ required: true, message: 'Vælg mindst én forsikringstype' }]}><Select mode="multiple" optionFilterProp="label" options={insuranceTypes.map((type) => ({ value: type.id, label: type.title }))} /></Form.Item>
        <Form.Item name="notes" label="Bemærkninger"><Input.TextArea rows={4} maxLength={2000} /></Form.Item>
        <Form.Item name="isActive" label="Aktiv" valuePropName="checked"><Switch checkedChildren="Ja" unCheckedChildren="Nej" /></Form.Item>
      </Form>
    </Modal>
  </Card>;
}